/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
export type LogLevel = 'error' | 'warn' | 'info' | 'debug';

const LEVEL_PRIORITY: Record<LogLevel, number> = {
    error: 0,
    warn: 1,
    info: 2,
    debug: 3,
};

const LEVEL_LABELS: Record<LogLevel, string> = {
    error: 'ERROR',
    warn: 'WARN',
    info: 'INFO',
    debug: 'DEBUG',
};

let overrideLevel: LogLevel | undefined;

/**
 * Memoised result of the environment-based resolution below.
 *
 * `shouldLog()` calls {@link resolveLevel} on *every* log invocation, including the ones it is about
 * to suppress — so without this, each dropped `logger.debug` in a loader or the SCAPI middleware
 * pays for an array literal and two string operations. Environment variables cannot change mid
 * process, so one resolution per process is correct. Cleared by {@link setLogLevel} so tests, which
 * do rewrite `process.env`, still see their changes.
 */
let resolvedLevel: LogLevel | undefined;

/**
 * Managed Runtime's two extra levels, mapped onto ours.
 *
 * MRT's "Log Filtering" setting offers `TRACE` and `FATAL` alongside the four this logger has.
 * Mapping them means the platform control does what it says — selecting `TRACE` in Runtime Admin
 * gets everything this logger can emit, instead of being rejected as unrecognised and silently
 * falling back to the production default.
 */
const LEVEL_ALIASES: Record<string, LogLevel> = {
    trace: 'debug',
    fatal: 'error',
};

/**
 * Coerce a raw environment value to a {@link LogLevel}, or `undefined` when it isn't one.
 *
 * Case-insensitive on purpose: MRT writes its levels uppercase (`INFO`), while `LEVEL_PRIORITY`'s
 * keys are lowercase, so a bare `in` check against that map rejects every value the platform sets.
 */
function normalizeLevel(raw: string | undefined): LogLevel | undefined {
    const value = raw?.trim().toLowerCase();
    if (!value) return undefined;
    if (value in LEVEL_ALIASES) return LEVEL_ALIASES[value];
    return value in LEVEL_PRIORITY ? (value as LogLevel) : undefined;
}

/**
 * Resolve the active log level.
 *
 * Order: an explicit {@link setLogLevel} override, then `MRT_LOG_LEVEL`, then `SFCC_LOG_LEVEL`,
 * then `warn` in production and `info` everywhere else.
 *
 * `MRT_LOG_LEVEL` keeps precedence because Managed Runtime's Log Filtering setting *also* gates what
 * it forwards to Log Center and to `tail-logs`. Emitting below that threshold is work whose output
 * the platform discards, so matching it is the coherent behaviour. `SFCC_LOG_LEVEL` is what governs
 * local development and anywhere else MRT hasn't injected its own value.
 */
export function resolveLevel(): LogLevel {
    if (overrideLevel) return overrideLevel;
    if (resolvedLevel) return resolvedLevel;

    if (typeof process !== 'undefined' && process.env) {
        // Each candidate is tried in turn rather than `MRT_LOG_LEVEL ?? SFCC_LOG_LEVEL`. `??` falls
        // through only on null/undefined, so a variable that is *set but unrecognised* used to
        // swallow the next one and drop straight to the production default. MRT injects
        // `MRT_LOG_LEVEL` uppercase on every deployed environment, and uppercase failed the
        // case-sensitive lookup below — so in production `SFCC_LOG_LEVEL` was never read and the
        // level was always `warn`, however either variable was configured (UCR-3022).
        for (const candidate of [process.env.MRT_LOG_LEVEL, process.env.SFCC_LOG_LEVEL]) {
            const level = normalizeLevel(candidate);
            if (level) return (resolvedLevel = level);
        }
        if (process.env.NODE_ENV === 'production') return (resolvedLevel = 'warn');
    }

    return (resolvedLevel = 'info');
}

function shouldLog(level: LogLevel): boolean {
    return LEVEL_PRIORITY[level] <= LEVEL_PRIORITY[resolveLevel()];
}

/**
 * Serialize an Error into a plain object so JSON.stringify doesn't produce "{}".
 */
export function serializeError(error: unknown): Record<string, unknown> {
    if (error instanceof Error) {
        return {
            name: error.name,
            message: error.message,
            ...(error.stack && { stack: error.stack }),
        };
    }
    return { value: String(error) };
}

/**
 * Process metadata: serialize Error instances found in values.
 */
export function processMetadata(metadata: Record<string, unknown>): Record<string, unknown> {
    const processed: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(metadata)) {
        processed[key] = value instanceof Error ? serializeError(value) : value;
    }
    return processed;
}

function mergeMetadata(
    base: Record<string, unknown> | undefined,
    extra: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
    if (!base && !extra) return undefined;
    if (!base) return extra;
    if (!extra) return base;
    return { ...base, ...extra };
}

/**
 * Structured logger interface used across the application.
 *
 * **Log level guidelines:**
 * - `error` — Unrecoverable failures within a request (e.g. uncaught exceptions, fatal API errors).
 * - `warn`  — Recoverable problems: fallback paths, retries, degraded behavior.
 * - `info`  — Observable outcomes: request completed, action succeeded/failed, significant state changes.
 *             Use for entries you want visible in production logs.
 * - `debug` — Internal progress: "starting X", "parsed Y", intermediate steps.
 *             Useful for local debugging but too noisy for production.
 */
export interface Logger {
    error(message: string, metadata?: Record<string, unknown>): void;
    warn(message: string, metadata?: Record<string, unknown>): void;
    info(message: string, metadata?: Record<string, unknown>): void;
    debug(message: string, metadata?: Record<string, unknown>): void;
}

/**
 * Create a level-gated logger for the template application.
 *
 * Output format: `LEVEL message {JSON metadata?}`
 *
 * Log level is controlled by `SFCC_LOG_LEVEL` (`error` | `warn` | `info` | `debug`), or by
 * `MRT_LOG_LEVEL` where Managed Runtime injects it — see {@link resolveLevel} for precedence.
 * Defaults to `warn` in production, `info` otherwise.
 *
 * Use this for client-side code or when router context is not available.
 * For server-side code with router context, prefer `getLogger` from `@/lib/logger.server`
 * which automatically includes the correlation ID.
 *
 * @param baseMetadata - Optional metadata included in every log entry (e.g. correlationId)
 *
 * @example
 * ```ts
 * const logger = createLogger();
 * logger.info('Variant selected', { sku });
 * ```
 */
export function createLogger(baseMetadata?: Record<string, unknown>): Logger {
    return Object.freeze({
        error(message: string, metadata?: Record<string, unknown>): void {
            if (!shouldLog('error')) return;
            const prefix = `${LEVEL_LABELS.error} ${message}`;
            const merged = mergeMetadata(baseMetadata, metadata ? processMetadata(metadata) : undefined);
            if (merged) {
                console.error(prefix, JSON.stringify(merged));
            } else {
                console.error(prefix);
            }
        },
        warn(message: string, metadata?: Record<string, unknown>): void {
            if (!shouldLog('warn')) return;
            const prefix = `${LEVEL_LABELS.warn} ${message}`;
            const merged = mergeMetadata(baseMetadata, metadata ? processMetadata(metadata) : undefined);
            if (merged) {
                console.warn(prefix, JSON.stringify(merged));
            } else {
                console.warn(prefix);
            }
        },
        info(message: string, metadata?: Record<string, unknown>): void {
            if (!shouldLog('info')) return;
            const prefix = `${LEVEL_LABELS.info} ${message}`;
            const merged = mergeMetadata(baseMetadata, metadata ? processMetadata(metadata) : undefined);
            if (merged) {
                console.log(prefix, JSON.stringify(merged));
            } else {
                console.log(prefix);
            }
        },
        debug(message: string, metadata?: Record<string, unknown>): void {
            if (!shouldLog('debug')) return;
            const prefix = `${LEVEL_LABELS.debug} ${message}`;
            const merged = mergeMetadata(baseMetadata, metadata ? processMetadata(metadata) : undefined);
            if (merged) {
                console.log(prefix, JSON.stringify(merged));
            } else {
                console.log(prefix);
            }
        },
    });
}

/**
 * Override the log level programmatically (useful for tests).
 * Pass `undefined` to reset to env-based resolution.
 */
export function setLogLevel(level: LogLevel | undefined): void {
    overrideLevel = level;
    // Also drop the memoised env resolution: passing `undefined` means "resolve from the environment
    // again", which is what tests do after rewriting `process.env`.
    resolvedLevel = undefined;
}

/**
 * Get the currently resolved log level.
 */
export function getLogLevel(): LogLevel {
    return resolveLevel();
}
